/**
 * ChallengeHub — Phase 4 UI of the Free-Batch build.
 *
 * Renders a compact, motivating "Referral Challenge" card on the
 * student home showing:
 *   • current referral count / target with a progress bar
 *   • 5-rung ladder (English / Physics / Chemistry / Biology tests
 *     then all discussions) — locked rungs dimmed, unlocked ones
 *     get a green check
 *   • the student's own referral code + WhatsApp share button +
 *     copy-to-clipboard
 *
 * Hides itself gracefully for non-free-batch students (endpoint
 * returns 400 NO_FREE_BATCH).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Lock, CheckCircle2, Share2, Copy, Check, Sparkles } from "lucide-react";
import { API } from "../contexts/AuthContext";

export default function ChallengeHub() {
  const [status, setStatus] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/me/free-batch/status`)
      .then((r) => { if (!cancelled) setStatus(r.data); })
      .catch(() => { /* silent — not a free-batch student */ });
    return () => { cancelled = true; };
  }, []);

  if (!status) return null;

  const {
    referral_code, referral_count, referral_target, referrals_remaining,
    progress_percent, unlock_sequence, next_unlock, is_complete, batch,
  } = status;

  const copyCode = () => {
    try { navigator.clipboard.writeText(referral_code); setCopied(true); setTimeout(() => setCopied(false), 1400); } catch { /* silent */ }
  };
  const shareWA = () => {
    const msg = `Hey! I&apos;m unlocking my LogicsWallah *${batch?.name || "free batch"}* one subject at a time — use my referral code *${referral_code}* when you register and we both level up. https://logicswallah.pk/free/${batch?.slug || ""}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div
      className="mb-8 rounded-[24px] p-6 md:p-7 relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #f0f9ff 0%, #ede9fe 100%)",
        border: "1px solid rgba(59,130,246,0.18)",
        boxShadow: "0 12px 30px -18px rgba(15,23,42,0.15)",
      }}
      data-testid="challenge-hub"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-[0.14em] uppercase mb-2"
               style={{ background: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd" }}>
            <Sparkles size={10} strokeWidth={2.5} /> Referral Challenge
          </div>
          <h2 className="text-[22px] md:text-[26px] font-bold leading-[1.15] tracking-[-0.02em] text-slate-900" data-testid="ch-title">
            {is_complete ? "Challenge complete!" : `Unlock ${next_unlock?.label || "your next reward"}`}
          </h2>
          <p className="mt-1 text-[13px] text-slate-600 leading-[1.5]">
            {is_complete
              ? "You've unlocked everything. Every new referral now goes towards helping a friend too."
              : `${referrals_remaining} more ${referrals_remaining === 1 ? "referral" : "referrals"} to unlock your next subject.`}
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[36px] font-black text-slate-900 leading-none" data-testid="ch-count">
            {referral_count}<span className="text-[18px] text-slate-400 font-semibold">/{referral_target}</span>
          </div>
          <div className="text-[10.5px] font-semibold tracking-[0.14em] uppercase text-slate-500 mt-1">
            Referrals
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2.5 rounded-full overflow-hidden mb-6"
           style={{ background: "rgba(15,23,42,0.06)" }}>
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${progress_percent}%`,
            background: "linear-gradient(90deg, #3b82f6 0%, #a855f7 60%, #ec4899 100%)",
          }}
          data-testid="ch-progress-bar"
        />
      </div>

      {/* Ladder */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 mb-6" data-testid="ch-ladder">
        {unlock_sequence.map((rung) => (
          <RungTile key={rung.referral_number} rung={rung} count={referral_count} />
        ))}
      </div>

      {/* Referral code + share */}
      <div className="p-4 rounded-[14px] bg-white flex items-center gap-3 flex-wrap"
           style={{ border: "1px dashed #3b82f6" }} data-testid="ch-code-card">
        <div className="min-w-0 flex-1">
          <div className="text-[9.5px] font-semibold tracking-[0.14em] uppercase text-blue-700 mb-1">
            Your referral code
          </div>
          <div className="text-[22px] font-black font-mono tracking-[0.20em] text-slate-900" data-testid="ch-code">
            {referral_code}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={copyCode}
            className="h-10 px-3 rounded-[10px] text-[12.5px] font-medium flex items-center gap-1.5 transition"
            style={{ background: "#f1f5f9", border: "1px solid rgba(15,23,42,0.10)", color: "#0f172a" }}
            data-testid="ch-copy"
          >
            {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
          </button>
          <button
            onClick={shareWA}
            className="h-10 px-3.5 rounded-[10px] text-[12.5px] font-semibold flex items-center gap-1.5 transition"
            style={{ background: "#25d366", color: "#fff" }}
            data-testid="ch-share"
          >
            <Share2 size={13} /> WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
}

function RungTile({ rung, count }) {
  const unlocked = count >= rung.referral_number;
  const bg = unlocked ? "#dcfce7" : "#fff";
  const border = unlocked ? "#22c55e" : "rgba(15,23,42,0.08)";

  return (
    <div
      className="p-3 rounded-[12px] flex flex-col items-start gap-1.5 relative"
      style={{ background: bg, border: `1px solid ${border}` }}
      data-testid={`ch-rung-${rung.referral_number}`}
    >
      <div className="flex items-center gap-1.5 text-[9.5px] font-semibold tracking-[0.12em] uppercase text-slate-500">
        <span>#{rung.referral_number}</span>
        {unlocked ? <CheckCircle2 size={11} className="text-emerald-600" strokeWidth={2.5} /> : <Lock size={10} className="text-slate-400" />}
      </div>
      <div className="text-[12px] font-semibold leading-[1.25] text-slate-900" title={rung.label}>
        {rung.label}
      </div>
    </div>
  );
}
